import { useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { EVENT_COLORS, getCategories } from "../lib/permissions";
import { fmtT } from "../lib/format";
import { Search, MapPin, Clock } from "lucide-react";

export default function LookupView({ events, onSelect }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const cats = getCategories();

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return events.filter(e => {
      if (picked.length && !picked.includes(e.event_type)) return false;
      if (needle && !`${e.title} ${e.event_type} ${e.address || ""}`.toLowerCase().includes(needle)) return false;
      return true;
    }).sort((a, b) => a.date.localeCompare(b.date));
  }, [events, q, picked]);

  const toggle = (name) =>
    setPicked(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);

  return (
    <div className="space-y-6" data-testid="lookup-view">
      <div>
        <h2 className="font-display text-2xl text-[var(--sn-gold)] flex items-center gap-2">
          <Search className="w-6 h-6" /> Event Lookup
        </h2>
        <p className="text-neutral-500 text-sm">Search by name or filter by one or more categories.</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
        <Input data-testid="lookup-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search event name, category, or address…"
          className="pl-9 bg-neutral-900 border-white/10 h-11" />
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="lookup-category-chips">
        {cats.map(c => {
          const active = picked.includes(c.name);
          const color = EVENT_COLORS[c.name];
          return (
            <button key={c.name} data-testid={`lookup-cat-${c.name}`} onClick={() => toggle(c.name)}
              className={`text-xs px-2.5 py-1.5 rounded-full border transition flex items-center gap-1.5 ${
                active ? "text-black font-semibold" : "text-neutral-400 bg-neutral-900 border-neutral-700 hover:border-neutral-500"
              }`}
              style={active ? { background: color?.dot, borderColor: color?.dot } : {}}>
              <span className="w-2 h-2 rounded-full" style={{ background: active ? "#000" : color?.dot }} />
              {c.name}
            </button>
          );
        })}
      </div>

      <div className="text-xs text-neutral-500 font-mono" data-testid="lookup-count">{results.length} event{results.length === 1 ? "" : "s"} found</div>

      <div className="space-y-2">
        {results.map(e => {
          const color = EVENT_COLORS[e.event_type];
          return (
            <button key={e.id} data-testid={`lookup-result-${e.id}`} onClick={() => onSelect(e)}
              className="w-full text-left glass-card rounded-xl p-4 hover:border-[var(--sn-gold)]/40 transition flex flex-wrap items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color?.dot }} />
              <div className="flex-1 min-w-[180px]">
                <div className="font-heading text-neutral-100">{e.title}</div>
                <div className="text-xs text-neutral-500 flex items-center gap-3 mt-0.5">
                  <span className="font-mono">{e.date}{e.end_date && e.end_date !== e.date ? ` → ${e.end_date}` : ""}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{e.all_day ? "All day" : `${fmtT(e.start_time)} – ${fmtT(e.end_time)}`}</span>
                  {e.address && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{e.address}</span>}
                </div>
              </div>
              <Badge className={color?.chip} style={color?.style}>{e.event_type}</Badge>
              {e.status === "pending" && <Badge className="bg-amber-950 text-amber-300 border-amber-700 border">pending</Badge>}
            </button>
          );
        })}
        {!results.length && (
          <Card className="glass-card p-10 text-center text-neutral-500">No events match your search.</Card>
        )}
      </div>
    </div>
  );
}
